
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, DOCUMENT} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Actions} from '@ngrx/effects';
import {CustomFormValidators} from '@spartacus/storefront';
import {ReCaptchaV3Service} from 'ng-recaptcha-2';
import {Subscription} from 'rxjs';

import {AimoRegistration, AimoRegistrationCompany} from '../../../model/user.model';
import {REGISTER_FAILED, REGISTER_SUCCESS, RegisterFailed} from '../../../service/user/aimo-user.action';
import {AimoUserService} from '../../../service/user/aimo-user.service';
import {AimoCustomFormValidators} from '../../../shared/validators/aimo-custom-form-validators';
import {AimoAuthService} from "../../../service/auth/aimo-auth.service";
import {take} from "rxjs/operators";

@Component({
    selector: 'aimo-login-register',
    templateUrl: './aimo-login-register.component.html',
    standalone: false
})
export class AimoLoginRegisterComponent implements OnInit, OnDestroy {
    private subscription = new Subscription();

    form: FormGroup;
    customerCounter = 0;
    registerDone = false;

    CUSTOMER = 'customer_';
    VAT = 'vat_';

    error: string;

    constructor(
        private actions$: Actions,
        protected cdr: ChangeDetectorRef,
        protected userService: AimoUserService,
        protected recaptchaV3Service: ReCaptchaV3Service,
        protected authService: AimoAuthService,
        @Inject(DOCUMENT) private document: Document,
    ) {
    }

    ngOnInit(): void {
        this.authService.isCDCLoginActivated()
            .pipe(take(1))
            .subscribe(cdcActivated => {
                const controls = {
                    name: new FormControl(null, [Validators.required]),
                    email: new FormControl(null, [Validators.required, AimoCustomFormValidators.emailValidator]),
                    phone: new FormControl(null, [Validators.required, AimoCustomFormValidators.phoneNumberValidator]),
                    acceptTerms: new FormControl(null, Validators.required),
                };

                const validators: ValidatorFn[] = [
                    AimoCustomFormValidators.bothRequiredOrEmptyValidator(this.VAT, this.CUSTOMER, 1)
                ];

                if (!cdcActivated) {
                    controls['password'] = new FormControl(null, [Validators.required, AimoCustomFormValidators.passwordValidator]);
                    controls['passwordConfirm'] = new FormControl(null, Validators.required);
                    validators.push(CustomFormValidators.passwordsMustMatch('password', 'passwordConfirm'));
                }

                this.form = new FormGroup(
                    controls,
                    {validators: validators}
                );
                this.addCustomer(true);
                this.subscription.add(
                    this.actions$.subscribe((action) => {
                        if (action.type === REGISTER_SUCCESS) {
                            this.registerDone = true;
                            this.scrollToTop();
                            this.cdr.detectChanges();
                        } else if (action.type === REGISTER_FAILED) {
                            this.error = (action as RegisterFailed).error;
                            this.cdr.detectChanges();
                        }
                    }),
                );
            });
    }


    addCustomer(required: boolean): void {
        const vatId = new FormControl(null, required ? [AimoCustomFormValidators.vatIdLengthValidator] : []);
        this.form.addControl(this.VAT + this.customerCounter, vatId);
        const customerId = new FormControl(null, required ? [
            Validators.required,
            CustomFormValidators.mustBePositive,
            Validators.maxLength(6),
        ] : null);
        this.form.addControl(this.CUSTOMER + this.customerCounter, customerId);
        this.customerCounter++;
        this.customerControls = undefined;
    }

    removeCustomer(): void {
        this.form.removeControl(this.VAT + (this.customerCounter - 1));
        this.form.removeControl(this.CUSTOMER + (this.customerCounter - 1));
        this.customerCounter--;
        this.customerControls = undefined;
    }

    customerControls: CustomerControl[];

    getCustomerControls(): CustomerControl[] {
        if (this.customerControls === undefined) {
            this.customerControls = [];
            for (let i = 0; i < this.customerCounter; ++i) {
                this.customerControls.push(
                    new CustomerControl(
                        i,
                        this.form.controls[this.VAT + i].value,
                        this.form.controls[this.CUSTOMER + i].value,
                    ),
                );
            }
        }
        return this.customerControls;
    }

    scrollToTop(): void {
        this.document.querySelector('#register-top-element').scrollIntoView();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();
        if (this.form.valid) {
            this.error = undefined;
            this.subscription.add(
                this.recaptchaV3Service.execute('importantAction').subscribe((token: string) => {
                    this.userService.register({
                        name: this.form.controls['name'].value,
                        email: this.form.controls['email'].value,
                        phone: this.form.controls['phone'].value,
                        password: this.form.controls['password']?.value,
                        companies: this.customersToForm(),
                        captchaToken: token,
                    } as AimoRegistration);
                }),
            );
        }
    }

    customersToForm(): AimoRegistrationCompany[] {
        const ret = [];
        for (let i = 0; i < this.customerCounter; ++i) {
            if (this.form.controls[this.VAT + i].value && this.form.controls[this.CUSTOMER + i].value) {
                ret.push({
                    vatId: this.form.controls[this.VAT + i].value,
                    customerId: this.form.controls[this.CUSTOMER + i].value,
                } as AimoRegistrationCompany);
            }
        }
        return ret;
    }
}

class CustomerControl {
    constructor(public idx: number, public vatId: FormControl, public customerId: FormControl) {
    }
}
